import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { ApiConnectionString } from "../../../constants/ApiConntection";
import { useAuthContext } from "../../../hooks/useAuthContext";

export default function CreateCoupon({ id, changeView }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [duration, setDuration] = useState("once");
  const [durationInMonths, setDurationInMonths] = useState("");
  const [discountType, setDiscountType] = useState("amount");
  const [amountOff, setAmountOff] = useState("");
  const [percentOff, setPercentOff] = useState("");
  const [redeemBy, setRedeemBy] = useState("");
  const [showRedeemBy, setShowRedeemBy] = useState(false);
  const [stripeProductIds, setStripeProductIds] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [appliesToObjects, setAppliesToObjects] = useState([]);
  const [showProductsField, setShowProductsField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subscription, setSubscription] = useState([]);
  const [payAsGo, setPayAsGo] = useState([]);

  const { user } = useAuthContext();
  const { clubId } = useParams();
  const [routineEvents, setRoutineEvents] = useState([]);
  const [oneOffEvents, setOneOffEvents] = useState([]);
  const [firstTimeTransaction, setFirstTimeTransaction] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const navi = useNavigate();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const prodResponse = await axios.get(
        `${ApiConnectionString}/api/myproducts?ClubId=${id}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setSubscription(prodResponse.data.filter((p) => p.recurring === true));
      setPayAsGo(prodResponse.data.filter((p) => p.recurring === false));

      const routineEventResponse = await axios.get(
        `${ApiConnectionString}/api/RoutineEventsByClub/${id}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setRoutineEvents(
        routineEventResponse.data.filter((e) => e.stripeProductId !== null)
      );

      const oneOffEventResponse = await axios.get(
        `${ApiConnectionString}/api/OneoffEventsByClub/${id}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const today = new Date().toISOString().split("T")[0];
      setOneOffEvents(
        oneOffEventResponse.data.filter(
          (e) => e.stripeProductId !== null && e.oneOffDate >= today
        )
      );
    } catch (error) {
      if (error.response?.status === 401) navi("/");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const handleCheckboxChange = (item) => {
    const stripeId = item.stripeProductId;
    const isChecked = stripeProductIds.includes(stripeId);

    if (isChecked) {
      setStripeProductIds((prev) => prev.filter((id) => id !== stripeId));
      setSelectedProducts((prev) =>
        prev.filter((p) => p.stripeProductId !== stripeId)
      );
      setAppliesToObjects((prev) =>
        prev.filter((p) => p.productId !== stripeId)
      );
    } else {
      setStripeProductIds((prev) => [...prev, stripeId]);
      setSelectedProducts((prev) => [...prev, item]);
      setAppliesToObjects((prev) => [
        ...prev,
        {
          productId: item.stripeProductId,
          name: item.name,
          price: item.price || 0,
          token: item.token ?? null,
          type: item.interval ? "sub" : item.token ? "flex" : "event",
          day: item.day ?? null,
          timeIn: item.timeIn ?? null,
          timeOut: item.timeOut ?? null,
          oneOffDate: item.oneOffDate ?? null,
        },
      ]);
    }
  };

  const resetForm = () => {
    setName("");
    setCode("");
    setDuration("once");
    setDurationInMonths("");
    setDiscountType("amount");
    setAmountOff("");
    setPercentOff("");
    setRedeemBy("");
    setShowRedeemBy(false);
    setStripeProductIds([]);
    setSelectedProducts([]);
    setAppliesToObjects([]);
    setShowProductsField(false);
    setFirstTimeTransaction(false);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(code || "");
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const data = {
      name,
      code,
      duration,
      durationInMonths:
        duration === "repeating" ? Number(durationInMonths) : null,
      amountOff:
        discountType === "amount" && amountOff ? Number(amountOff) : null,
      percentOff:
        discountType === "percent" && percentOff ? Number(percentOff) : null,
      redeemBy: showRedeemBy && redeemBy ? new Date(redeemBy) : null,
      products: showProductsField ? stripeProductIds : [],
      couponAppliesTos: appliesToObjects ? appliesToObjects : null,
      firstTimeTransaction: firstTimeTransaction,
    };

    try {
      const couponRes = await axios.post(
        `${ApiConnectionString}/api/Coupon`,
        data,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
      if (couponRes.status === 200) {
        setIsComplete(true);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  if (isComplete) {
    return (
      <div className="container bg-white p-4 rounded shadow-sm">
        <div className="alert alert-success mb-4">Coupon created</div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">{name}</h5>
            <div className="mb-2">
              <span className="fw-bold">Code</span>: {code}{" "}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ms-2"
                onClick={handleCopyCode}
              >
                Copy
              </button>
            </div>
            <div className="mb-2">
              <span className="fw-bold">Discount</span>:{" "}
              {discountType === "amount"
                ? `£${amountOff || 0}`
                : `${percentOff || 0}%`}
            </div>
            <div className="mb-2">
              <span className="fw-bold">Duration</span>:{" "}
              {duration === "repeating"
                ? `Repeating for ${durationInMonths} months`
                : duration}
            </div>
            {showRedeemBy && redeemBy && (
              <div className="mb-2">
                <span className="fw-bold">Redeem by</span>: {redeemBy}
              </div>
            )}
            {firstTimeTransaction && (
              <div className="badge bg-info text-dark">
                First time purchase only
              </div>
            )}
          </div>
        </div>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setIsComplete(false);
            }}
          >
            Create another
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => changeView("MainPage")}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-md-10 col-lg-6">
        <a
          className="text-decoration-none d-inline-flex align-items-center mb-3"
          onClick={() => changeView("MainPage")}
        >
          ← Go back
        </a>

        <div className="card shadow-sm border-0 rounded-4">
          <div className="card-body p-4 p-md-5">
            <h4 className="mb-4">Create a discount coupon</h4>

            <form onSubmit={handleSubmit}>
              <div className="pb-3 mb-3 border-bottom">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <input
                  id="name"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="pb-3 mb-3 border-bottom">
                <label htmlFor="code" className="form-label">
                  Code
                </label>
                <input
                  id="code"
                  className="form-control"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
                <div className="form-text">Shown to customers at checkout</div>
              </div>

              <div className="pb-3 mb-3 border-bottom">
                <label htmlFor="duration" className="form-label">
                  Duration
                </label>
                <select
                  id="duration"
                  className="form-select"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                >
                  <option value="once">Once</option>
                  <option value="forever">Forever</option>
                  <option value="repeating">Repeating</option>
                </select>
                <div className="form-text">
                  This setting applies only to <strong>subscriptions</strong>.
                  <br />
                  <strong>Once</strong> means it applies to the first payment
                  only,
                  <strong>Forever</strong> means it applies to every payment
                  with no time limit, and <strong>Repeating</strong> means it
                  applies for a set number of months.
                </div>
              </div>

              {duration === "repeating" && (
                <div className="pb-3 mb-3 border-bottom">
                  <label htmlFor="durationInMonths" className="form-label">
                    Duration in Months
                  </label>
                  <input
                    id="durationInMonths"
                    type="number"
                    min="1"
                    className="form-control"
                    value={durationInMonths}
                    onChange={(e) => setDurationInMonths(e.target.value)}
                    required
                  />
                  <div className="form-text">
                    Please select how many months this coupon will apply to a
                    subscription.
                  </div>
                </div>
              )}

              <div className="pb-3 mb-3 border-bottom">
                <div className="form-label">Discount Type</div>
                <div className="d-flex flex-wrap align-items-center gap-4 mb-3">
                  <div className="form-check">
                    <input
                      id="discountAmount"
                      name="discountType"
                      className="form-check-input"
                      type="radio"
                      value="amount"
                      checked={discountType === "amount"}
                      onChange={() => setDiscountType("amount")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="discountAmount"
                    >
                      Amount Off (£)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      id="discountPercent"
                      name="discountType"
                      className="form-check-input"
                      type="radio"
                      value="percent"
                      checked={discountType === "percent"}
                      onChange={() => setDiscountType("percent")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="discountPercent"
                    >
                      Percent Off (%)
                    </label>
                  </div>
                </div>

                {discountType === "amount" && (
                  <div className="input-group">
                    <span className="input-group-text">£</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={amountOff}
                      onChange={(e) => setAmountOff(e.target.value)}
                      placeholder="Enter amount off"
                      required
                    />
                  </div>
                )}

                {discountType === "percent" && (
                  <div className="input-group">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      className="form-control"
                      value={percentOff}
                      onChange={(e) => setPercentOff(e.target.value)}
                      placeholder="Enter percent off"
                      required
                    />
                    <span className="input-group-text">%</span>
                  </div>
                )}

                <div className="form-text">
                  Amount applies before tax. Percent must be 1 to 100.
                </div>
              </div>

              <div className="pb-3 mb-3 border-bottom">
                {!showRedeemBy ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowRedeemBy(true)}
                    >
                      Add expiry date
                    </button>
                    <div className="form-text">
                      Optional — set a last date when this coupon can be
                      redeemed.
                    </div>
                  </>
                ) : (
                  <>
                    <label htmlFor="redeemBy" className="form-label">
                      Redeem by
                    </label>
                    <input
                      id="redeemBy"
                      type="date"
                      className="form-control"
                      value={redeemBy}
                      onChange={(e) => setRedeemBy(e.target.value)}
                    />
                    <div className="form-text">
                      After this date, customers will no longer be able to use
                      this coupon.
                    </div>
                    <button
                      type="button"
                      className="btn btn-link p-0 mt-1"
                      onClick={() => {
                        setShowRedeemBy(false);
                        setRedeemBy("");
                      }}
                    >
                      Remove expiry date
                    </button>
                  </>
                )}
              </div>

              <div className="pb-3 mb-3 border-bottom">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="firstTimeTransaction"
                    checked={firstTimeTransaction}
                    onChange={(e) => setFirstTimeTransaction(e.target.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="firstTimeTransaction"
                  >
                    First purchase only
                  </label>
                  <div className="form-text">
                    If selected, customers can only use this coupon on their
                    very first purchase with your club.
                  </div>
                </div>
              </div>

              <div className="pb-3 mb-3 border-bottom">
                {!showProductsField ? (
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowProductsField(true)}
                    >
                      Specify products
                    </button>
                    <div className="form-text">
                      By default this coupon applies to all products. Click{" "}
                      <strong>Specify products</strong> if you want to limit it
                      to certain subscriptions, packs, or events only.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold mb-0">Select products</h6>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setShowProductsField(false);
                          setStripeProductIds([]);
                          setSelectedProducts([]);
                          setAppliesToObjects([]);
                        }}
                      >
                        Apply to all products
                      </button>
                    </div>

                    <div className="accordion mb-3" id="prodAcc">
                      <div className="accordion-item">
                        <h2 className="accordion-header" id="accSubHead">
                          <button
                            className={`accordion-button${
                              showProductsField ? "" : " collapsed"
                            }`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#accSubBody"
                            aria-expanded={showProductsField ? "true" : "false"}
                            aria-controls="accSubBody"
                          >
                            Subscription Products
                          </button>
                        </h2>
                        <div
                          id="accSubBody"
                          className={`accordion-collapse collapse${
                            showProductsField ? " show" : ""
                          }`}
                          data-bs-parent="#prodAcc"
                          aria-labelledby="accSubHead"
                        >
                          <div className="accordion-body">
                            <ul className="list-group">
                              {subscription.map((prod) => (
                                <li key={prod.id} className="list-group-item">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`prod-${prod.id}`}
                                      checked={stripeProductIds.includes(
                                        prod.stripeProductId
                                      )}
                                      onChange={() =>
                                        handleCheckboxChange(prod)
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`prod-${prod.id}`}
                                    >
                                      <strong>{prod.name}</strong>
                                      <br />
                                      {prod.interval
                                        ? `Interval: ${prod.interval}`
                                        : `Tokens: ${prod.token}`}
                                    </label>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item">
                        <h2 className="accordion-header" id="accPaygHead">
                          <button
                            className={`accordion-button${
                              showProductsField ? "" : " collapsed"
                            }`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#accPaygBody"
                            aria-expanded={showProductsField ? "true" : "false"}
                            aria-controls="accPaygBody"
                          >
                            Flexible Class Pack Options
                          </button>
                        </h2>
                        <div
                          id="accPaygBody"
                          className={`accordion-collapse collapse${
                            showProductsField ? " show" : ""
                          }`}
                          data-bs-parent="#prodAcc"
                          aria-labelledby="accPaygHead"
                        >
                          <div className="accordion-body">
                            <ul className="list-group">
                              {payAsGo.map((prod) => (
                                <li key={prod.id} className="list-group-item">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`payg-${prod.id}`}
                                      checked={stripeProductIds.includes(
                                        prod.stripeProductId
                                      )}
                                      onChange={() =>
                                        handleCheckboxChange(prod)
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`payg-${prod.id}`}
                                    >
                                      <strong>{prod.name}</strong>
                                      <br />
                                      {prod.interval
                                        ? `Interval: ${prod.interval}`
                                        : `Tokens: ${prod.token}`}
                                    </label>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="accordion mb-3" id="eventAcc">
                      <div className="accordion-item">
                        <h2 className="accordion-header" id="accRoutineHead">
                          <button
                            className={`accordion-button${
                              showProductsField ? "" : " collapsed"
                            }`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#accRoutineBody"
                            aria-expanded={showProductsField ? "true" : "false"}
                            aria-controls="accRoutineBody"
                          >
                            Routine Events
                          </button>
                        </h2>
                        <div
                          id="accRoutineBody"
                          className={`accordion-collapse collapse${
                            showProductsField ? " show" : ""
                          }`}
                          data-bs-parent="#eventAcc"
                          aria-labelledby="accRoutineHead"
                        >
                          <div className="accordion-body">
                            {routineEvents.map((event) => (
                              <div className="form-check mb-2" key={event.id}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`re-${event.id}`}
                                  checked={stripeProductIds.includes(
                                    event.stripeProductId
                                  )}
                                  onChange={() => handleCheckboxChange(event)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`re-${event.id}`}
                                >
                                  <strong>{event.name}</strong>
                                  <br />
                                  {event.day} | {event.timeIn}–{event.timeOut}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item">
                        <h2 className="accordion-header" id="accOneOffHead">
                          <button
                            className={`accordion-button${
                              showProductsField ? "" : " collapsed"
                            }`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#accOneOffBody"
                            aria-expanded={showProductsField ? "true" : "false"}
                            aria-controls="accOneOffBody"
                          >
                            One Off Events
                          </button>
                        </h2>
                        <div
                          id="accOneOffBody"
                          className={`accordion-collapse collapse${
                            showProductsField ? " show" : ""
                          }`}
                          data-bs-parent="#eventAcc"
                          aria-labelledby="accOneOffHead"
                        >
                          <div className="accordion-body">
                            {oneOffEvents.map((event) => (
                              <div className="form-check mb-2" key={event.id}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`oe-${event.id}`}
                                  checked={stripeProductIds.includes(
                                    event.stripeProductId
                                  )}
                                  onChange={() => handleCheckboxChange(event)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`oe-${event.id}`}
                                >
                                  <strong>{event.name}</strong>
                                  <br />
                                  {event.oneOffDate} | {event.timeIn}–
                                  {event.timeOut}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="d-grid">
                <button type="submit" className="btn btn-primary btn-lg">
                  Create Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
